/**
 * Stripe Payment Dialog Component
 * 
 * Opens Stripe Checkout in a new tab and polls for payment completion.
 * This allows the user to stay on the current page while completing payment.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CreditCard, Shield, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { apiClient } from '../../lib/api/client';

interface StripePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  checkoutUrl: string;
  purchaseId: string;
  amount?: number;
  description?: string;
  title?: string;
  onSuccess?: (result: { credit_amount: number; new_balance: number }) => void;
  onCancel?: () => void;
}

type PaymentStatus = 'idle' | 'waiting' | 'completed' | 'failed';

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_TIME = 10 * 60 * 1000; // 10 minutes max wait

export function StripePaymentDialog({
  open,
  onOpenChange,
  checkoutUrl,
  purchaseId,
  amount,
  description,
  title,
  onSuccess,
  onCancel,
}: StripePaymentDialogProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [result, setResult] = useState<{ credit_amount: number; new_balance: number } | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number>(0);
  const checkoutWindowRef = useRef<Window | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Start polling when dialog opens
  useEffect(() => {
    if (open && checkoutUrl && purchaseId && status === 'idle') {
      openCheckoutAndStartPolling();
    }
  }, [open, checkoutUrl, purchaseId]);

  const openCheckoutAndStartPolling = useCallback(() => {
    // Open Stripe Checkout in new tab
    checkoutWindowRef.current = window.open(checkoutUrl, '_blank');
    
    setStatus('waiting');
    pollStartTimeRef.current = Date.now();
    
    // Start polling for purchase status
    pollIntervalRef.current = setInterval(async () => {
      // Check if we've been polling too long
      if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
        stopPolling();
        setStatus('failed');
        return;
      }
      
      try {
        const statusResult = await apiClient.credits.getPurchaseStatus(purchaseId);
        
        if (statusResult.status === 'completed') {
          stopPolling();
          setStatus('completed');
          setResult({
            credit_amount: statusResult.credit_amount || 0,
            new_balance: statusResult.new_balance || 0,
          });
          
          // Close checkout tab if still open
          if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
            checkoutWindowRef.current.close();
          }
        } else if (statusResult.status === 'failed') {
          stopPolling();
          setStatus('failed');
        }
        // If still 'pending', continue polling
      } catch (error) {
        console.error('Error checking purchase status:', error);
        // Continue polling on error (could be temporary network issue)
      }
    }, POLL_INTERVAL);
  }, [checkoutUrl, purchaseId]);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const handleClose = () => {
    stopPolling();
    
    if (status === 'completed' && result) {
      onSuccess?.(result);
    } else {
      onCancel?.();
    }
    
    setStatus('idle');
    setResult(null);
    onOpenChange(false);
  };

  const handleCancel = () => {
    stopPolling();
    
    // Close checkout tab if still open
    if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
      checkoutWindowRef.current.close();
    }
    
    onCancel?.();
    setStatus('idle');
    setResult(null);
    onOpenChange(false);
  };

  const handleOpenCheckout = () => {
    // Reopen checkout tab
    checkoutWindowRef.current = window.open(checkoutUrl, '_blank');
  };

  // Don't render anything if dialog is closed
  if (!open) {
    return null;
  }

  const dialogTitle = title || t('payment.completePayment', 'Complete Payment');

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleCancel()}>
      <DialogContent className="payment-dialog sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status === 'completed' ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : status === 'failed' ? (
              <XCircle className="h-5 w-5 text-destructive" />
            ) : (
              <CreditCard className="h-5 w-5" />
            )}
            {status === 'completed' 
              ? t('payment.paymentSuccessful', 'Payment Successful!')
              : status === 'failed'
              ? t('payment.paymentFailed', 'Payment Failed')
              : dialogTitle
            }
          </DialogTitle>
          <DialogDescription>
            {status === 'waiting' 
              ? t('payment.completeInNewTab', 'Complete your payment in the new tab. This dialog will update automatically.')
              : status === 'completed'
              ? t('payment.creditsAddedToBalance', 'Your credits have been added to your balance.')
              : status === 'failed'
              ? t('payment.paymentDidNotComplete', 'The payment did not complete. Please try again.')
              : t('payment.securePoweredByStripe', 'Secure payment powered by Stripe')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Payment Summary */}
          {amount && status !== 'completed' && (
            <div className="payment-summary bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  {t('payment.amountToPay', 'Amount to pay:')}
                </span>
                <span className="text-2xl font-bold">${amount.toFixed(2)}</span>
              </div>
              {description && (
                <p className="text-sm text-muted-foreground mt-1">{description}</p>
              )}
            </div>
          )}

          {/* Status Display */}
          {status === 'waiting' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground text-center">
                {t('payment.waitingForPayment', 'Waiting for payment confirmation...')}
              </p>
              <p className="text-xs text-muted-foreground text-center">
                {t('payment.dontCloseDialog', "Don't close this dialog until payment is complete.")}
              </p>
            </div>
          )}

          {status === 'completed' && result && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold">
                  +${result.credit_amount.toFixed(2)} {t('credits.inCredits', 'in credits')}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t('payment.newBalance', 'New balance')}: ${result.new_balance.toFixed(2)}
                </p>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <XCircle className="h-10 w-10 text-destructive" />
              </div>
              <p className="text-sm text-muted-foreground text-center">
                {t('payment.tryAgainOrContact', 'Please try again or contact support if the problem persists.')}
              </p>
            </div>
          )}

          {/* Security Notice */}
          {status === 'waiting' && (
            <div className="security-notice flex items-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span>{t('payment.securedByStripe', 'Your payment is secured by Stripe')}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-2 space-y-2">
            {status === 'waiting' && (
              <>
                <Button
                  onClick={handleOpenCheckout}
                  variant="outline"
                  className="w-full"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('payment.reopenPaymentPage', 'Reopen Payment Page')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  className="w-full"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </>
            )}
            
            {status === 'completed' && (
              <Button onClick={handleClose} className="w-full">
                {t('common.continue', 'Continue')}
              </Button>
            )}
            
            {status === 'failed' && (
              <>
                <Button onClick={handleOpenCheckout} className="w-full">
                  {t('payment.tryAgain', 'Try Again')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
